import { useState, useEffect, useCallback, RefObject, useLayoutEffect } from 'react';

const DROPDOWN_PREFERRED_HEIGHT = 350;
const DROPDOWN_GAP = 4;

type DropdownStyle = {
  top: number;
  left: number;
  width: number;
} | null;

type UseDropdownPositionReturn = {
  dropdownStyle: DropdownStyle;
  maxHeight: number;
  isReady: boolean;
};

/**
 * Custom hook to calculate dropdown position and max height
 * Handles positioning above or below based on available space
 */
export const useDropdownPosition = (
  isOpen: boolean,
  containerRef: RefObject<HTMLDivElement>,
  dropdownRef: RefObject<HTMLDivElement>
): UseDropdownPositionReturn => {
  const [maxHeight, setMaxHeight] = useState(DROPDOWN_PREFERRED_HEIGHT);
  const [dropdownStyle, setDropdownStyle] = useState<DropdownStyle>(null);
  const [isReady, setIsReady] = useState(false);
  const updateDropdownPosition = useCallback((): void => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom - DROPDOWN_GAP;
      const spaceAbove = rect.top - DROPDOWN_GAP;

      const actualHeight = dropdownRef.current?.getBoundingClientRect().height ?? 0;
      const desiredHeight = actualHeight || DROPDOWN_PREFERRED_HEIGHT;

      // Calculate position
      let position: 'bottom' | 'top' = 'bottom';

      // Position above only if the current size doesn't fit below
      if (spaceBelow < desiredHeight && spaceAbove > spaceBelow) {
        position = 'top';
      }

      const availableSpace = position === 'bottom' ? spaceBelow : spaceAbove;
      const calculatedMaxHeight = Math.min(DROPDOWN_PREFERRED_HEIGHT, availableSpace);

      setMaxHeight(calculatedMaxHeight);

      // Calculate absolute position for portal
      if (actualHeight > 0) {
        setIsReady(true);
      }
      const top =
        position === 'bottom'
          ? rect.bottom + DROPDOWN_GAP
          : rect.top - (actualHeight || calculatedMaxHeight) - DROPDOWN_GAP;
      setDropdownStyle({
        top,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [isOpen, containerRef, dropdownRef]);

  useEffect(() => {
    if (!isOpen) {
      setIsReady(false);
    }
  }, [isOpen]);

  useLayoutEffect(() => {
    updateDropdownPosition();

    if (!isOpen) {
      return;
    }

    const animationFrameId = requestAnimationFrame(() => {
      updateDropdownPosition();
    });
    const animationTimeoutId = window.setTimeout(() => {
      updateDropdownPosition();
    }, 150);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.clearTimeout(animationTimeoutId);
    };
  }, [isOpen, updateDropdownPosition]);

  // This could be added, if we want to make the dropdown height dynamic. It will cause the container to reattach to the select field on filtering
  // useEffect(() => {
  //   if (!isOpen || !dropdownRef.current) {
  //     return;
  //   }

  //   const observer = new ResizeObserver(() => {
  //     updateDropdownPosition();
  //   });

  //   observer.observe(dropdownRef.current);

  //   return () => {
  //     observer.disconnect();
  //   };
  // }, [isOpen, dropdownRef, updateDropdownPosition]);

  // Update position on scroll/resize when dropdown is open
  useEffect(() => {
    if (isOpen) {
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);

      return () => {
        window.removeEventListener('scroll', updateDropdownPosition, true);
        window.removeEventListener('resize', updateDropdownPosition);
      };
    }
  }, [isOpen, updateDropdownPosition]);

  return { dropdownStyle, maxHeight, isReady };
};
